import type { UserManager } from 'oidc-client-ts';
import type { Market } from '@//config/domain-locale-map';
import { isLocalStorageAvailable } from '@//utils/safe-storage';

const CLIENT_ROOT = typeof window === 'object' ? window.location.origin : '/';

const REDIRECT_PATH_SIGN_IN = '/oidc/signin';
const REDIRECT_PATH_SIGN_OUT = '/oidc/signout';

const OIDC_AUTHORITIES = {
  WAYKE: process.env.NEXT_PUBLIC_OIDC_AUTHORITY_WAYKE,
  DRIVE: process.env.NEXT_PUBLIC_OIDC_AUTHORITY_DRIVE,
};

let userManager: UserManager | undefined;

// Resolves once createUserManager has built the UserManager. Sign-in/out flows
// triggered by user interaction can fire before AuthProvider's mount effect has
// initialized it — the effect runs after paint and awaits a dynamic import of
// oidc-client-ts. Awaiting this lets those flows wait instead of crashing on a
// fast click.
let markUserManagerReady: (manager: UserManager) => void = () => {};
const userManagerReady = new Promise<UserManager>((resolve) => {
  markUserManagerReady = resolve;
});

export const createUserManager = async (market: Market): Promise<UserManager> => {
  const authority = OIDC_AUTHORITIES[market.toUpperCase() as keyof typeof OIDC_AUTHORITIES];
  if (!authority) throw new Error(`Issuer not found for market: ${market}`);
  const client_id = process.env.NEXT_PUBLIC_OIDC_CLIENT_ID;
  if (!client_id) throw new Error(`Client ID not found for market`);
  const response_type = process.env.NEXT_PUBLIC_OIDC_RESPONSE_TYPE;
  if (!response_type) throw new Error(`Response type not found for market`);
  const scope = process.env.NEXT_PUBLIC_OIDC_SCOPE;
  if (!scope) throw new Error(`Scope not found for market`);

  const { UserManager, WebStorageStateStore, InMemoryWebStorage } = await import('oidc-client-ts');

  // Safari blocks localStorage entirely in some privacy contexts (ITP,
  // "block all cookies"), throwing SecurityError on any access. Fall back to an
  // in-memory store so the app still loads — the OIDC session just will not
  // persist across reloads in that case.
  const store = isLocalStorageAvailable() ? window.localStorage : new InMemoryWebStorage();

  userManager = new UserManager({
    authority,
    client_id,
    response_type,
    scope,
    redirect_uri: `${CLIENT_ROOT}${REDIRECT_PATH_SIGN_IN}`,
    post_logout_redirect_uri: `${CLIENT_ROOT}${REDIRECT_PATH_SIGN_OUT}`,
    loadUserInfo: false,
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store, prefix: `oidc.${market}.` }),
    stateStore: new WebStorageStateStore({ store, prefix: `oidc.${market}.` }),
    acr_values: 'source:web-ui-app',
  });
  markUserManagerReady(userManager);
  return userManager;
};

/**
 * Resolves with the UserManager once it has been initialized. Use this from
 * code paths that may run before AuthProvider has finished initializing — e.g.
 * a sign-in button clicked right after the page becomes interactive — so they
 * wait for initialization instead of throwing.
 */
export const getUserManagerAsync = (): Promise<UserManager> =>
  userManager ? Promise.resolve(userManager) : userManagerReady;
